import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { useContentTrends } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Professional color palette
const FEATURE_COLORS: Record<string, { line: string; bg: string }> = {
  private_prompts: { line: '#3b82f6', bg: '#3b82f620' },  // blue
  public_prompts: { line: '#8b5cf6', bg: '#8b5cf620' },   // violet
  collections: { line: '#10b981', bg: '#10b98120' },      // emerald
  comments: { line: '#f59e0b', bg: '#f59e0b20' },         // amber
  votes: { line: '#ef4444', bg: '#ef444420' },            // red
  api_calls: { line: '#06b6d4', bg: '#06b6d420' },        // cyan
};

const FEATURE_NAMES: Record<string, string> = {
  private_prompts: 'Private Prompts',
  public_prompts: 'Public Prompts',
  collections: 'Collections',
  comments: 'Comments',
  votes: 'Votes',
  api_calls: 'API Calls',
};

interface Props {
  dateRange: DateRange;
}

/**
 * Usage Trends Chart
 * Professional line chart showing content activity over time
 */
export function UsageTrendsChart({ dateRange }: Props) {
  const { data, isLoading, error } = useContentTrends(dateRange);

  // Transform data from array to grouped by date
  const chartData = {} as Record<string, any>;
  const featureKeys = new Set<string>();

  (data || []).forEach((item) => {
    if (!chartData[item.date]) {
      chartData[item.date] = { date: item.date };
    }
    chartData[item.date][item.feature_key] = item.total_usage;
    featureKeys.add(item.feature_key);
  });

  const transformedData = Object.values(chartData).sort((a: any, b: any) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Get unique feature keys for dynamic lines
  const features = Array.from(featureKeys);

  // Calculate totals for summary
  const totals = features.reduce((acc, feature) => {
    acc[feature] = transformedData.reduce((sum: number, day: any) => sum + (day[feature] || 0), 0);
    return acc;
  }, {} as Record<string, number>);

  const TrendIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
    </svg>
  );

  return (
    <Card
      title="Content Activity Trends"
      subtitle="Daily content creation and engagement metrics"
      isLoading={isLoading}
      error={error?.message || null}
      icon={TrendIcon}
      accentColor="from-emerald-500 to-teal-500"
    >
      {transformedData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4" />
          </svg>
          <p className="text-sm font-medium">No activity data for this period</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={transformedData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                {features.map((feature) => (
                  <linearGradient key={`gradient-${feature}`} id={`gradient-${feature}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={FEATURE_COLORS[feature]?.line || '#6b7280'} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={FEATURE_COLORS[feature]?.line || '#6b7280'} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
                interval={Math.max(0, Math.floor(transformedData.length / 6))}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
                  padding: '12px 16px',
                }}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                }}
                formatter={(value: any, name: string) => [
                  value.toLocaleString(),
                  FEATURE_NAMES[name] || name.replace(/_/g, ' ')
                ]}
              />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="circle"
                iconSize={8}
                formatter={(value: string) => (
                  <span className="text-xs text-gray-600 font-medium">
                    {FEATURE_NAMES[value] || value.replace(/_/g, ' ')}
                  </span>
                )}
              />
              {features.map((feature) => (
                <Area
                  key={`area-${feature}`}
                  type="monotone"
                  dataKey={feature}
                  stroke={FEATURE_COLORS[feature]?.line || '#6b7280'}
                  fill={`url(#gradient-${feature})`}
                  strokeWidth={0}
                />
              ))}
              {features.map((feature) => (
                <Line
                  key={feature}
                  type="monotone"
                  dataKey={feature}
                  stroke={FEATURE_COLORS[feature]?.line || '#6b7280'}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: 'white' }}
                  name={feature}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>

          {/* Summary stats */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-2">
            {features.map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-2 p-3 rounded-lg bg-gray-50/80 hover:bg-gray-100/80 transition-colors"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: FEATURE_COLORS[feature]?.line || '#6b7280' }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 truncate">
                    {FEATURE_NAMES[feature] || feature.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {(totals[feature] || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
